import { Fragment } from 'react';
import { Keyboard } from '@launchpad-ui/components';

import { parseHotkey } from '../parse-hotkey';
import { physicalKeyCodeToFriendlyKeyName } from '../physical-key-code-to-friendly-key-name';
import type { KeyStep } from '../types';

import styles from './keyboard-shortcut.module.css';

export function KeyboardShortcut({
  shortcut,
  usePhysicalKeys = true,
}: {
  shortcut: string;
  usePhysicalKeys?: boolean;
}) {
  const parsed = parseHotkey(shortcut, usePhysicalKeys);

  // Normalize to a list of steps. A sequence is multiple steps (press
  // one chord, then the next, e.g. `g then f`); a plain shortcut is a
  // single step. Each step is a chord of one or more keys pressed
  // together (e.g. `shift /`).
  const steps = parsed.type === 'sequence' ? parsed.steps : [parsed.steps];

  // Readable label for assistive tech, e.g. "Meta a" or "g then i".
  // The visual keycaps are split across separate <kbd>s with no text
  // between them (spacing is CSS gap), so without this the accessible
  // name would collapse to "Metaa" / "gtheni".
  const ariaLabel = steps.map((step) => formatKeyStep(step, usePhysicalKeys).join(' ')).join(' then ');

  // Per the MDN convention for representing keystrokes: an outer <kbd>
  // wraps the whole input, and each individual key is its own nested
  // <kbd>. So `shift /` renders as two keycaps -- [⇧][/] -- rather than
  // a single box, and a sequence puts "then" between its steps.
  return (
    <Keyboard className={styles.shortcut} aria-label={ariaLabel}>
      {steps.map((step, stepIndex) => (
        <Fragment key={stepIndex}>
          {formatKeyStep(step, usePhysicalKeys).map((key, keyIndex) => (
            <Keyboard key={keyIndex} className={styles.key}>
              {key}
            </Keyboard>
          ))}
          {stepIndex < steps.length - 1 && <span className={styles.then}>then</span>}
        </Fragment>
      ))}
    </Keyboard>
  );
}

function formatKeyStep(step: KeyStep, usePhysicalKeys: boolean = true): string[] {
  const parts: string[] = [];

  if (step.modifiers.alt) {
    parts.push('Alt');
  }
  if (step.modifiers.ctrl) {
    parts.push('Control');
  }
  if (step.modifiers.shift) {
    parts.push('Shift');
  }
  if (step.modifiers.meta) {
    parts.push('Meta');
  }

  parts.push(step.key);

  return usePhysicalKeys ? parts.map(physicalKeyCodeToFriendlyKeyName) : parts;
}
